import React from 'react';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import {
  Message, MessageType, Partner, TripMatch, TripRequest, TripRequestType,
} from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../../store';
import useZicassoChat from '../../../../hooks/use-zicasso-chat';
import ConversationView from './conversation-view';
import debugPromise from '../../../../utils/debug-promise';

type Props = {
  conversationId?: string | undefined,
  conversationTitle?: string,
  tripMatch: TripMatch | undefined,
};

const ConversationContainer = ({
  conversationId = undefined,
  conversationTitle = undefined,
  tripMatch = undefined,
}: Props) => {
  const { loggedInTraveler } = useAuthStore();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { tripMatchId } = useParams();

  const {
    isError,
    isLoading,
    isValidating,
    conversation,
    messages,
    lastSeenMessageId,
    participants,
    sendMessage,
    updateMessages,
    loadMoreMessages,
    hasMoreMessages,
  } = useZicassoChat({
    conversationId,
    viewerId: loggedInTraveler?.id,
    tripMatchId,
    tripRequestId: tripMatch?.tripRequestId,
  });
  const viewer = {
    id: loggedInTraveler?.id,
    type: PersonType.Traveler,
  };

  const localMatch = state?.data?.match;
  const { data: loadedMatch } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const match = localMatch || loadedMatch;
  const { data: tripRequest } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [match?.tripRequestId], !!match?.tripRequestId);
  const { data: partner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [match?.partnerId], !!match?.partnerId);

  const handleOnSend = (messageText: string) => {
    debugPromise('tripMatchConversationContainer:sendMessage', sendMessage({
      conversationId,
      text: messageText,
      messageType: MessageType.Text,
    }));
  };

  const handleDocumentClick = async (message: Message) => {
    if (!message.entityId) {
      return;
    }
    // To prevent pop-up from being blocked (e.g. Safari)
    const popupWindow = window.open('', '_blank');
    const notifyBlockedPopup = () => {
      enqueueSnackbar('Please enable pop-ups to view the document.');
    };
    const openInPopup = (url: string) => {
      if (popupWindow) {
        popupWindow.location.href = url;
      } else {
        notifyBlockedPopup();
      }
    };

    try {
      const doc = await apiClient.documentsClient.getDocument(message.entityId);
      if (doc.sourceURI) {
        openInPopup(doc.sourceURI);
        return;
      }

      const downloadURL = await apiClient.documentsClient.getDocumentDownloadUrl(message.entityId);
      openInPopup(downloadURL.url);
    } catch (error: any) {
      if (popupWindow) {
        popupWindow.close();
      }
      const snackbarMessage = error?.code === 404 ? 'Document not found' : 'Error downloading document';
      enqueueSnackbar(snackbarMessage, { variant: 'default' });
    }
  };

  const handleTripMatchReviewRequestClick = async (message: Message) => {
    if (message.entityId) {
      navigate(`/trip-matches/${message.entityId}/review`);
    }
  };

  const handleViewClick = (message: Message) => {
    switch (message.entityType) {
      case 'Document':
        debugPromise('tripMatchConversationContainer:viewClick.document', handleDocumentClick(message));
        break;
      case 'TripMatchReviewRequest':
        debugPromise('tripMatchConversationContainer:viewClick.tripMatchReviewRequest', handleTripMatchReviewRequestClick(message));
        break;
      case 'SalesRecording':
        break;
      default:
        break;
    }
  };

  return (
    <ConversationView
      conversation={conversation}
      conversationTitle={conversationTitle}
      handleViewClick={handleViewClick}
      isError={isError}
      isLoading={isLoading}
      isValidating={isValidating}
      messages={messages}
      lastMessageSeenId={lastSeenMessageId}
      loadMoreMessages={loadMoreMessages}
      hasMoreMessages={hasMoreMessages}
      participants={participants}
      sendMessage={handleOnSend}
      viewer={viewer}
      updateMessages={updateMessages}
      tripMatch={tripMatch}
      partner={partner}
      isLegacyTR={tripRequest?.tripRequestType === TripRequestType.Legacy}
    />
  );
};

export default ConversationContainer;
